import { ReactComponent as DeleteIcon } from '@epam/assets/icons/common/action-deletelight-18.svg'
import { ReactComponent as EditIcon } from '@epam/assets/icons/common/content-edit-18.svg'
import {
  Anchor,
  FlexCell,
  FlexRow,
  FlexSpacer,
  IconButton,
  LabeledInput,
  Text,
  TextArea,
  TextInput,
} from '@epam/loveship'
import { ReactComponent as MapIcon } from '../../../../icons/google_maps_icon.svg'
import { useUuiContext, UuiContexts } from '@epam/uui-core'
import { SidebarPanelHeader } from '../../../../components/SidebarPanel/components/SidebarPanelHeader'
import { SidebarPanelMain } from '../../../../components/SidebarPanel/components/SidebarPanelMain'
import { PointResponse, PointReviewResponse } from '../../../../constants/types'
import { useSideBarControlls } from '../../../../hooks/useSideBarControlls'
import { useDeletePointMutation } from '../../../../store'
import { getGoogleMapsUrl } from '../../../../utils/common/getGoogleMapsUrl'
import { showConfirmPointDeleteModal } from '../../../../utils/modals/showConfirmPointDeleteModal'
import { useAppSelector } from '../../../../hooks/redux/redux'
import { useActions } from '../../../../hooks/redux/actions'
import { LOCATION_NOTFOUND_MESSAGE } from '../../../../constants/values'
import { showDeletePointIsNotPossibleModal } from '../../../../utils/modals/showDeletePointIsNotPossibleModal'

type PointDetailsProps = {
  point: PointResponse
  pointCategory?: string
  reviews: PointReviewResponse[] | undefined
}

export const PointDetails = ({
  point,
  pointCategory,
  reviews,
}: PointDetailsProps) => {
  const { openEditPoint, showPointsPanel } = useSideBarControlls()
  const svc = useUuiContext<UuiContexts>()
  const { setSelectedPoint, setSelectedPointId } = useActions()
  const { userInfo: user } = useAppSelector((state) => state.users)
  const googleMapsUrl = getGoogleMapsUrl(point.lat, point.lng)
  const [deletePoint] = useDeletePointMutation()
  const { removeCurrentPoint } = useActions()

  const onDelete = (pointId: number) => {
    const hasNonAuthorReviews = reviews?.some(
      (review) => review.creator.id !== user?.id
    )

    if (hasNonAuthorReviews) {
      showDeletePointIsNotPossibleModal(svc).catch(() => null)
      return
    }

    const deletePointHandler = () => {
      setSelectedPoint(null)
      setSelectedPointId(null)
      return deletePoint(pointId)
        .unwrap()
        .then(() => {
          removeCurrentPoint(pointId)
        })
    }

    showConfirmPointDeleteModal(svc, deletePointHandler)
      .then(() => showPointsPanel())
      .catch(() => null)
  }

  return (
    <>
      <SidebarPanelHeader
        title="Place Info"
        onClose={showPointsPanel}
        withCloseIcon
      />

      <SidebarPanelMain withBorderBottom>
        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput label="Place Name">
              <TextArea
                isReadonly
                value={point.name}
                onValueChange={() => {}}
                rows={1}
                autoSize
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput label="Place Category">
              <TextInput
                isReadonly
                value={pointCategory}
                onValueChange={() => {}}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput label="Place Description">
              <TextArea
                isReadonly
                value={point.description}
                onValueChange={() => {}}
                rows={1}
                autoSize
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput label="Place Location">
              <TextArea
                isReadonly
                value={point.location || LOCATION_NOTFOUND_MESSAGE}
                onValueChange={() => {}}
                rows={1}
                autoSize
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow>
          <Anchor
            rawProps={{ 'aria-label': 'Google Maps' }}
            href={googleMapsUrl}
            target="_blank"
          >
            <Text size="36" font="sans" color="night700">
              <MapIcon style={{ height: 12, width: 12 }} /> Open in Google Maps
            </Text>
          </Anchor>
          <FlexSpacer />
          {point.creator && point.creator.id === user?.id && (
            <FlexRow spacing="6">
              <IconButton icon={EditIcon} onClick={openEditPoint} />
              <IconButton
                icon={DeleteIcon}
                onClick={() => onDelete(point.id)}
              />
            </FlexRow>
          )}
        </FlexRow>
      </SidebarPanelMain>
    </>
  )
}
